import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ComponentCard from '../../components/common/ComponentCard';
import Label from '../../components/form/Label';
import { useAuth } from '../../context/AuthContext';
import { api, apiEndpoints } from '../../utils/apiService';

type KnowledgeBaseArtifact = {
  id: number;
  store: number;
  job_id: number;
  status: 'draft' | 'needs_review' | 'approved' | 'superseded';
  model_name: string;
  markdown_content: string;
  has_markdown: boolean;
  generation_metadata?: Record<string, unknown>;
  source_file_ids: number[];
  approved_by: number | null;
  approved_at: string | null;
  parent_artifact: number | null;
  superseded_by?: number | null;
  created_at: string;
  updated_at: string;
};

type FeedbackSuccess = {
  feedback_id: number;
  feedback_status: string;
  artifact: KnowledgeBaseArtifact;
  chapters_merged: string[];
  chapters_unchanged: string[];
  change_summary: string[];
  warnings: string[];
};

type FeedbackErrorPayload = {
  detail?: string;
  feedback_id?: number;
  max_bytes?: number;
  errors?: string[];
  warnings?: string[];
  reason?: string;
  superseded_by?: number;
};

type LocationState = {
  feedbackResult?: FeedbackSuccess;
};

type CompilerSummary = {
  compiler?: { enabled?: boolean };
  blocked_reasons?: string[];
};

const getResponseError = async (response: Response, fallback: string) => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.detail || data.message || data.error || fallback;
  }
  return fallback;
};

const readJsonPayload = async <T,>(response: Response): Promise<T> =>
  (await response.json().catch(() => ({}))) as T;

const formatBytes = (bytes?: number | null) => {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

const parseFeedbackError = async (response: Response) => {
  const data = await readJsonPayload<FeedbackErrorPayload>(response);
  const detail = data.detail || 'Feedback submission failed.';
  const auditText = data.feedback_id
    ? ` Feedback audit row #${data.feedback_id}.`
    : '';

  switch (response.status) {
    case 400:
    case 415:
      return detail;
    case 409:
      return data.superseded_by
        ? `This draft was superseded by Draft #${data.superseded_by}. Recompile to submit feedback on the latest.`
        : detail;
    case 413:
      return `${detail}${
        data.max_bytes
          ? ` Max upload size: ${formatBytes(data.max_bytes)}.`
          : ''
      } Please split the feedback into smaller files.`;
    case 422:
      return [
        detail,
        ...(data.errors || []),
        ...(data.warnings || []).map((warning) => `Warning: ${warning}`),
        auditText.trim(),
      ]
        .filter(Boolean)
        .join('\n');
    case 502:
      return `Merge failed - saved as audit row #${
        data.feedback_id || 'unknown'
      } for engineer review.${data.reason ? ` Reason: ${data.reason}` : ''}`;
    case 503:
      return detail;
    default:
      return `${detail}${auditText}`;
  }
};

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Never';

const statusBadgeClassName = (status: KnowledgeBaseArtifact['status']) => {
  switch (status) {
    case 'approved':
      return 'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/20 dark:text-success-400 dark:ring-success-800';
    case 'draft':
    case 'needs_review':
      return 'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-900/20 dark:text-warning-400 dark:ring-warning-800';
    case 'superseded':
      return 'bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
  }
};

const KnowledgeBaseArtifactViewer: React.FC = () => {
  const { artifactId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [artifact, setArtifact] = useState<KnowledgeBaseArtifact | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [feedbackDocx, setFeedbackDocx] = useState<File | null>(null);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] =
    useState<FeedbackSuccess | null>(
      (location.state as LocationState | null)?.feedbackResult || null,
    );
  const [compilerEnabled, setCompilerEnabled] = useState(true);
  const [compilerBlockedReasons, setCompilerBlockedReasons] = useState<
    string[]
  >([]);
  const [error, setError] = useState('');

  const feedbackDisabledReason = useMemo(() => {
    if (!compilerEnabled) {
      return (
        compilerBlockedReasons.join(' · ') ||
        'Knowledge base compilation is currently disabled.'
      );
    }
    if (artifact?.status === 'superseded') {
      return 'This draft was superseded. Submit feedback on the latest draft.';
    }
    if (!feedbackComments.trim() && !feedbackDocx) {
      return 'Add comments or attach an edited .docx.';
    }
    return '';
  }, [
    artifact?.status,
    compilerBlockedReasons,
    compilerEnabled,
    feedbackComments,
    feedbackDocx,
  ]);

  const canSubmitFeedback =
    !!artifact &&
    artifact.status !== 'superseded' &&
    compilerEnabled &&
    (!!feedbackComments.trim() || !!feedbackDocx) &&
    !feedbackSubmitting;

  const fetchArtifact = useCallback(async () => {
    if (!artifactId || !user?.is_platform_admin) return;

    setLoading(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.getKnowledgeBaseArtifact(artifactId),
      );
      if (response?.ok) {
        setArtifact(await response.json());
      } else if (response?.status === 404) {
        setError('Artifact not found for the selected store.');
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to load artifact (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to load artifact: No response from server.');
      }
    } catch (err) {
      console.error('Knowledge base artifact load error:', err);
      setError('An unexpected error occurred loading this artifact.');
    } finally {
      setLoading(false);
    }
  }, [artifactId, user?.is_platform_admin]);

  useEffect(() => {
    fetchArtifact();
  }, [fetchArtifact]);

  useEffect(() => {
    setFeedbackError('');
    setFeedbackComments('');
    setFeedbackDocx(null);
    setFeedbackSuccess(
      (location.state as LocationState | null)?.feedbackResult || null,
    );
  }, [artifactId, location.state]);

  useEffect(() => {
    const fetchCompilerState = async () => {
      if (!user?.is_platform_admin) return;

      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseDashboardCompile(),
        );
        if (response?.ok) {
          const data = (await response.json()) as CompilerSummary;
          setCompilerEnabled(data.compiler?.enabled !== false);
          setCompilerBlockedReasons(data.blocked_reasons || []);
        }
      } catch (err) {
        console.error('Knowledge base compiler state load error:', err);
      }
    };

    fetchCompilerState();
  }, [user?.is_platform_admin]);

  const downloadMarkdown = async () => {
    if (!artifactId) return;

    setDownloading(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.downloadKnowledgeBaseArtifact(artifactId),
      );
      if (response?.ok) {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `knowledge-base-artifact-${artifactId}.md`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to download markdown (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to download markdown: No response from server.');
      }
    } catch (err) {
      console.error('Knowledge base artifact download error:', err);
      setError('An unexpected error occurred downloading the markdown.');
    } finally {
      setDownloading(false);
    }
  };

  const exportDocx = async () => {
    if (!artifactId) return;

    setExportingDocx(true);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.exportKnowledgeBaseArtifactDocx(artifactId),
      );
      if (response?.ok) {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `artifact-${artifactId}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } else if (response?.status === 404) {
        setError('Artifact not found or has no markdown to export.');
      } else if (response?.status === 502) {
        setError('DOCX export failed. Please try again.');
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to export DOCX (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to export DOCX: No response from server.');
      }
    } catch (err) {
      console.error('Knowledge base artifact DOCX export error:', err);
      setError('An unexpected error occurred exporting the DOCX.');
    } finally {
      setExportingDocx(false);
    }
  };

  const submitFeedback = async () => {
    if (!artifactId || !canSubmitFeedback) return;

    const formData = new FormData();
    const comments = feedbackComments.trim();
    if (comments) formData.append('comments', comments);
    if (feedbackDocx) formData.append('edited_docx', feedbackDocx);

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 130000);

    setFeedbackSubmitting(true);
    setFeedbackError('');
    setError('');
    try {
      const response = await api.postForm(
        apiEndpoints.submitKnowledgeBaseArtifactFeedback(artifactId),
        formData,
        { signal: controller.signal },
      );

      if (response?.status === 201) {
        const data = (await response.json()) as FeedbackSuccess;
        setFeedbackSuccess(data);
        navigate(`/artifacts/${data.artifact.id}/`, {
          state: { feedbackResult: data } satisfies LocationState,
        });
      } else if (response) {
        setFeedbackError(await parseFeedbackError(response));
      } else {
        setFeedbackError('Failed to submit feedback: No response from server.');
      }
    } catch (err) {
      console.error('Knowledge base feedback submit error:', err);
      setFeedbackError(
        err instanceof DOMException && err.name === 'AbortError'
          ? 'Feedback merge is taking longer than expected. Please try again or ask engineering to check server logs.'
          : 'An unexpected error occurred submitting feedback.',
      );
    } finally {
      window.clearTimeout(timeout);
      setFeedbackSubmitting(false);
    }
  };

  if (!user?.is_platform_admin) {
    return (
      <ComponentCard
        title="Knowledge Base Artifact"
        className="overflow-hidden"
      >
        <div className="p-4 text-sm text-error-500">
          This tool is only available to platform admins.
        </div>
      </ComponentCard>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Knowledge Base Artifact
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Review generated markdown for artifact #{artifactId}.
          </p>
        </div>
        <Link
          to="/platform-admin"
          className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Back to Platform Admin
        </Link>
      </div>

      {error ? (
        <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
          {error}
        </div>
      ) : null}

      {loading ? (
        <ComponentCard title="Loading" className="overflow-hidden">
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            Loading artifact...
          </div>
        </ComponentCard>
      ) : artifact ? (
        <>
          <ComponentCard
            title={`Artifact #${artifact.id}`}
            className="overflow-hidden"
          >
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Status
                </p>
                <span
                  className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadgeClassName(
                    artifact.status,
                  )}`}
                >
                  {artifact.status}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Model
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                  {artifact.model_name || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Source Files
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                  {artifact.source_file_ids.length}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Created
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                  {formatDateTime(artifact.created_at)}
                </p>
              </div>
            </div>
            {artifact.parent_artifact || artifact.superseded_by ? (
              <div className="mt-5 flex flex-wrap gap-2">
                {artifact.parent_artifact ? (
                  <Link
                    to={`/artifacts/${artifact.parent_artifact}/`}
                    className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200 underline-offset-2 hover:underline dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-800"
                  >
                    From feedback on Draft #{artifact.parent_artifact}
                  </Link>
                ) : null}
                {artifact.superseded_by ? (
                  <Link
                    to={`/artifacts/${artifact.superseded_by}/`}
                    className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 underline-offset-2 hover:underline dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700"
                  >
                    Superseded by Draft #{artifact.superseded_by}
                  </Link>
                ) : null}
              </div>
            ) : null}
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={downloadMarkdown}
                disabled={downloading || !artifact.has_markdown}
                className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading ? 'Downloading...' : 'Download Markdown'}
              </button>
              <button
                type="button"
                onClick={exportDocx}
                disabled={exportingDocx || !artifact.has_markdown}
                title="Download a sanitized .docx the restaurant can edit in Google Docs."
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {exportingDocx
                  ? 'Exporting...'
                  : 'Export for Restaurant Review'}
              </button>
              {!artifact.has_markdown ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This artifact does not include markdown content.
                </p>
              ) : null}
            </div>
          </ComponentCard>

          {feedbackSuccess ? (
            <ComponentCard title="What Changed" className="overflow-hidden">
              <div className="space-y-4">
                <div className="rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400">
                  Created Draft #{feedbackSuccess.artifact.id} from feedback
                  round #{feedbackSuccess.feedback_id}.
                </div>
                {feedbackSuccess.change_summary.length ? (
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      Change summary
                    </p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
                      {feedbackSuccess.change_summary.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Chapters merged
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      {feedbackSuccess.chapters_merged.join(', ') || 'None'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Chapters unchanged
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                      {feedbackSuccess.chapters_unchanged.join(', ') || 'None'}
                    </p>
                  </div>
                </div>
                {feedbackSuccess.warnings.length ? (
                  <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
                    <p className="font-semibold">Warnings</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {feedbackSuccess.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </ComponentCard>
          ) : null}

          {artifact.status !== 'superseded' ? (
            <ComponentCard
              title="Submit Restaurant Feedback"
              className="overflow-hidden"
            >
              <div className="space-y-5">
                {artifact.status === 'approved' ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                    Submitting feedback on the approved KB will create a new
                    draft. The currently-approved KB stays live until you
                    approve the new draft.
                  </div>
                ) : null}

                {!compilerEnabled ? (
                  <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
                    {feedbackDisabledReason}
                  </div>
                ) : null}

                {feedbackError ? (
                  <div className="whitespace-pre-wrap rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
                    {feedbackError}
                  </div>
                ) : null}

                <div>
                  <Label>Free-form notes from the restaurant</Label>
                  <textarea
                    value={feedbackComments}
                    onChange={(event) =>
                      setFeedbackComments(event.target.value)
                    }
                    disabled={feedbackSubmitting}
                    rows={5}
                    placeholder="Paste restaurant comments or review notes here."
                    className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                  />
                </div>

                <div>
                  <Label>Edited .docx</Label>
                  <input
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    disabled={feedbackSubmitting}
                    onChange={(event) =>
                      setFeedbackDocx(event.target.files?.[0] || null)
                    }
                    className="block w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-gray-700 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:file:bg-gray-800 dark:file:text-gray-200"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Optional. Max 10 MiB. Upload the edited DOCX the restaurant
                    reviewed in Google Docs or Word.
                  </p>
                </div>

                {feedbackSubmitting ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                    Merging feedback into a new draft. This can take 10-60
                    seconds.
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={submitFeedback}
                  disabled={!canSubmitFeedback}
                  title={feedbackDisabledReason || undefined}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {feedbackSubmitting
                    ? 'Submitting feedback...'
                    : 'Submit Restaurant Feedback'}
                </button>
              </div>
            </ComponentCard>
          ) : null}

          <ComponentCard title="Markdown Preview" className="overflow-hidden">
            <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-2xl bg-gray-950 p-5 text-sm leading-6 text-gray-100">
              {artifact.markdown_content || 'No markdown content.'}
            </pre>
          </ComponentCard>
        </>
      ) : null}
    </div>
  );
};

export default KnowledgeBaseArtifactViewer;
